import { useMutation, useQuery } from "@tanstack/solid-query";
import { createContext, createSignal, useContext } from "solid-js";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { getPublicMessageFromError } from "~/solidJs/shared";
import { PaymentApi, type PlanChangeRequest } from "~/solidJs/shared/api/PaymentApi";
import { UserSubscriptionPlanEnum } from "~/solidJs/shared/helpers/types";
import { showToastMessage, ToastMessageTypes } from "../WorkspaceToast/WorkspaceToast";
import type {
	ChangeSubscriptionPeriod,
	ConfirmPlanChangeModalData,
	ConfirmPlanChangeState
} from "./model";
import { SUBSCRIPTION_DOWNGRADE_PERIODS, SUBSCRIPTION_UPGRADE_PERIODS } from "./model";

// Global modal state - available everywhere
const [getIsModalVisible, setIsModalVisible] = createSignal<false | ConfirmPlanChangeModalData>(
	false
);

// Global function to show modal from anywhere
export const showConfirmPlanChangeModal = (
	plan: UserSubscriptionPlanEnum,
	mode: "upgrade" | "downgrade"
) => {
	setIsModalVisible({ plan, mode });
};

// Global function to hide modal
export const hideConfirmPlanChangeModal = () => {
	setIsModalVisible(false);
};

// Global getters for modal state
export const getConfirmPlanChangeModalState = () => getIsModalVisible();
export const isConfirmPlanChangeModalOpen = () => Boolean(getIsModalVisible());

export type ConfirmPlanChangeContextType = ReturnType<typeof useConfirmPlanChangeStore>;

export const ConfirmPlanChangeContext = createContext<ConfirmPlanChangeContextType>();

export const useConfirmPlanChangeStore = () => {
	const [getSelectedPeriod, setSelectedPeriod] = createSignal<ChangeSubscriptionPeriod>(
		SUBSCRIPTION_DOWNGRADE_PERIODS[0]
	);

	const [getIsShowSuccessModal, setIsShowSuccessModal] = createSignal(false);

	const getPlan = () => {
		const modalData = getIsModalVisible();
		return modalData ? modalData.plan : UserSubscriptionPlanEnum.Starter;
	};

	const getModalMode = () => {
		const modalData = getIsModalVisible();
		return modalData ? modalData.mode : "upgrade";
	};

	const confirmMutation = useMutation(() => ({
		key: "confirmPlanChange",
		mutationFn: async () => {
			const isVisible = getIsModalOpen();
			if (!isVisible) return;

			return await PaymentApi.changeUserPlan({
				planId: getPlan(),
				startUpgrading: getSelectedPeriod().value
			});
		},
		onError: (error) => {
			showToastMessage({
				type: ToastMessageTypes.Error,
				title: getPublicMessageFromError(error) || "Unknown error"
			});
		},
		onMutate() {
			EventAnalytics.subscriptionUiInteracted("upgrade");
		},
		onSuccess(data) {
			EventAnalytics.subscriptionUiInteracted("change_plan");
			setIsShowSuccessModal(true);
			if (data?.redirect_url) {
				const el = Object.assign(document.createElement("a"), {
					target: "_blank",
					rel: "noopener noreferrer",
					href: data.redirect_url
				});
				document.body.append(el);
				el.click();
				el.remove();
			}
		}
	}));

	const getSubscriptionPeriod = () => getSelectedPeriod();

	const getSelectedPeriodItem = (): ChangeSubscriptionPeriod => {
		return getSelectedPeriod();
	};

	const getAvailablePeriods = () =>
		getModalMode() === "upgrade"
			? SUBSCRIPTION_UPGRADE_PERIODS
			: SUBSCRIPTION_DOWNGRADE_PERIODS;

	const getIsModalOpen = () => Boolean(getIsModalVisible());

	const hideModal = () => {
		resetToDefaults();
		hideConfirmPlanChangeModal();
	};

	const setSubscriptionPeriodByItem = (item: ChangeSubscriptionPeriod) => {
		setSelectedPeriod(item);
	};

	// Computed values
	const getState = (): ConfirmPlanChangeState => ({
		plan: getPlan(),
		selectedPeriod: getSubscriptionPeriod()
	});

	const resetToDefaults = () => {
		setIsShowSuccessModal(false);
		setSelectedPeriod(SUBSCRIPTION_DOWNGRADE_PERIODS[0]);
	};

	return {
		getPlan,
		getSubscriptionPeriod,
		getSelectedPeriodItem,
		getAvailablePeriods,
		getIsModalOpen,
		getState,
		getModalMode,

		// Setters
		hideModal,
		setSubscriptionPeriodByItem,

		// Actions
		resetToDefaults,
		confirmMutation,
		getIsShowSuccessModal
	};
};

export const useConfirmPlanContext = () => {
	const store = useContext(ConfirmPlanChangeContext);
	if (!store) {
		throw new Error("ConfirmPlanChangeContext is not available");
	}
	return store;
};

export const usePlanChangeDetails = () => {
	const store = useConfirmPlanContext();

	const planChangeRequest = (): PlanChangeRequest => ({
		targetPlan: store.getPlan(),
		upgradeInterval: store.getSubscriptionPeriod().value
	});

	const query = useQuery(() => PaymentApi.getPlanChangeDetailsOptions(planChangeRequest()));

	return query;
};
